/* eslint-disable react/no-unstable-nested-components */
import { FC, useMemo, useState } from 'react';
import { Column, Table, TableCellProps } from 'react-virtualized';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Checkbox, Input, useThemeValue } from '@altermaind/web/ui';
import { useDebounce } from '@/_shared/hooks/useDebounce';
import 'react-virtualized/styles.css';

type Props = {
  tableWidth?: number;
  tableHeight?: number;
  data: {
    id: string;
    [key: string]: string | number | boolean | undefined;
  }[];
  showCheckboxes?: boolean;
  checkBoxesCallback?: (selectedIds: string[]) => void;
  headerLocales?: string;
  initialSelectedIds?: string[];
  isSearchable?: boolean;
  searchLabel?: string;
  columnWidths?: { [key: string]: number };
};

type RowData = {
  id: string;
  [key: string]: string | number | boolean | undefined;
};

const CheckboxCellRenderer: FC<{
  rowData: RowData;
  isChecked: boolean;
  onToggle: (id: string) => void;
}> = ({ rowData, isChecked, onToggle }) => {
  return <Checkbox checked={isChecked} onClick={() => onToggle(rowData.id)} />;
};

export const VirtualizedTable: FC<Props> = ({
  data,
  showCheckboxes,
  checkBoxesCallback,
  tableWidth,
  tableHeight,
  headerLocales,
  initialSelectedIds,
  isSearchable = false,
  searchLabel,
  columnWidths,
}) => {
  const t = useTranslations(headerLocales);
  const tShared = useTranslations();
  const fontSizeValue = useThemeValue('fontSizes', 's');
  const colorValue = useThemeValue('colors', 'grey/light');
  const paddingBottomValue = useThemeValue('space', 's');

  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds ?? []);
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearch = useDebounce((value: string) => {
    setSearchQuery(value);
  }, 300);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const lower = searchQuery.toLowerCase();
    return data.filter(row =>
      Object.values(row).some(val => typeof val === 'string' && val.toLowerCase().includes(lower)),
    );
  }, [data, searchQuery]);

  const toggleCheckbox = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    if (checkBoxesCallback) {
      const newSelected = selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id];
      checkBoxesCallback(newSelected);
    }
  };

  const headerStyle = {
    backgroundColor: 'transparent',
    color: colorValue,
    fontWeight: '500',
    textAlign: 'left' as const,
    fontSize: fontSizeValue,
    textTransform: 'capitalize' as const,
    paddingBottom: paddingBottomValue,
  };

  const rowStyle = {
    color: 'white',
    fontWeight: '400',
  };

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        flexDirection: 'column',
      }}
    >
      {isSearchable && (
        <Box width='100%' mb='s'>
          <Input
            suffixSvg='search'
            svgStyle={{ fill: 'grey/light' }}
            inputTextStyle={{ pr: 'l' }}
            type='text'
            placeholder={searchLabel ?? tShared('inputs.placeholderSearch')}
            onChange={e => debouncedSearch(e.target.value)}
          />
        </Box>
      )}
      <Box style={{ width: '100%', overflowX: 'auto' }}>
        <Table
          width={tableWidth || 600}
          height={tableHeight || 260}
          headerHeight={40}
          rowHeight={40}
          rowCount={filteredData.length}
          rowGetter={({ index }) => filteredData[index]}
          headerStyle={headerStyle}
          rowStyle={rowStyle}
        >
          {showCheckboxes && (
            <Column
              key='checkbox'
              dataKey='checkbox'
              width={50}
              flexGrow={0}
              cellRenderer={({ rowData }: TableCellProps) => (
                <CheckboxCellRenderer
                  rowData={rowData as RowData}
                  isChecked={selectedIds.includes((rowData as RowData).id)}
                  onToggle={toggleCheckbox}
                />
              )}
            />
          )}
          {filteredData.length > 0 &&
            Object.keys(filteredData[0])
              .filter(key => key !== 'id')
              .map(key => (
                <Column
                  key={key}
                  label={t(`${key}`)}
                  dataKey={key}
                  width={columnWidths?.[key] ?? 150}
                  flexGrow={1}
                />
              ))}
        </Table>
      </Box>
    </Box>
  );
};
